import { useEffect, useState } from "react";
import api, { formatApiError, downloadPdf } from "@/lib/api";
import { SUBJECTS, DAYS, subjectStyle } from "@/lib/constants";
import { Plus, Trash2, X, Download } from "lucide-react";
import { toast } from "sonner";

const empty = { subject: "Mathématiques", day: 0, start_time: "08:00", end_time: "09:00", room: "", teacher: "" };

export default function Timetable() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/timetable").then(({ data }) => setEntries(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/timetable", { ...form, day: Number(form.day) });
      toast.success("Cours ajouté à ton emploi du temps");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/timetable/${id}`);
      toast.success("Cours supprimé");
      setEntries(entries.filter((e) => e.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
  const weekDays = DAYS.slice(0, 6);

  return (
    <div className="space-y-6" data-testid="timetable-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Emploi du temps</h1>
          <p className="text-slate-500 text-sm sm:text-base mt-1">Ta semaine, organisée au quart d'heure près.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => downloadPdf("/export/timetable.pdf", "mon-emploi-du-temps-proedu.pdf").then(() => toast.success("PDF téléchargé !")).catch(() => toast.error("Export impossible"))}
            data-testid="export-timetable-pdf"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-colors duration-200"
          >
            <Download className="h-4 w-4" /> PDF
          </button>
          <button
            onClick={() => setOpen(true)}
            data-testid="add-course-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200"
          >
            <Plus className="h-4 w-4" /> Ajouter un cours
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse grid md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-64 bg-slate-100 rounded-3xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {weekDays.map((day, idx) => {
            const dayEntries = entries
              .filter((e) => e.day === idx)
              .sort((a, b) => a.start_time.localeCompare(b.start_time));
            const isToday = idx === todayIdx;
            return (
              <div
                key={day}
                data-testid={`timetable-day-${idx}`}
                className={`rounded-3xl border p-4 min-h-[180px] ${isToday ? "bg-indigo-50/60 border-indigo-200" : "bg-white border-slate-200/80"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-semibold text-sm ${isToday ? "text-indigo-700" : "text-slate-800"}`}>{day}</h3>
                  {isToday && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-600 text-white">Aujourd'hui</span>}
                </div>
                {dayEntries.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-8">Libre</p>
                ) : (
                  <ul className="space-y-2">
                    {dayEntries.map((e) => (
                      <li key={e.id} data-testid={`timetable-entry-${e.id}`} className={`group relative rounded-xl border p-3 ${subjectStyle(e.subject)}`}>
                        <p className="text-xs font-bold">{e.start_time} – {e.end_time}</p>
                        <p className="text-sm font-semibold mt-0.5">{e.subject}</p>
                        {(e.room || e.teacher) && (
                          <p className="text-[11px] opacity-75 mt-0.5">{[e.room, e.teacher].filter(Boolean).join(" · ")}</p>
                        )}
                        <button
                          onClick={() => remove(e.id)}
                          data-testid={`timetable-delete-${e.id}`}
                          className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-white/70 transition-opacity duration-200"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="course-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-slate-900">Nouveau cours</h2>
              <button onClick={() => setOpen(false)} data-testid="course-modal-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Matière</label>
                <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="course-subject-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Jour</label>
                <select value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="course-day-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                  {weekDays.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Début</label>
                  <input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="course-start-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Fin</label>
                  <input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="course-end-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Salle (optionnel)</label>
                  <input type="text" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="course-room-input" placeholder="B204" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Professeur (optionnel)</label>
                  <input type="text" value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} data-testid="course-teacher-input" placeholder="Mme Dupont" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
              </div>
              <button type="submit" data-testid="course-submit-button" className="w-full py-3 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200">
                Ajouter le cours
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
